import { ChangeDetectorRef, Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';

import { NgxSpinnerService } from 'ngx-spinner';

import { forkJoin } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import * as CookieConsent from 'vanilla-cookieconsent';

import { mapAsBody } from '../../../shared/util/request-util';

import { TransportItem } from '../../../../models/transport-item.model';
import { PATHS } from '../../../../models/bary-paths.model';
import { TransportFormGroup } from '../../../../models/forms/TransportFormGroup';
import { OfferEstimation } from '../../../../models/offer-estimation.model';
import { Transporter } from '../../../../models/transporter.model';
import { Transport } from '../../../../models/transport.model';
import { TransportViewDTO } from '../../../../models/transport-view-dto.model';

import { CurrentDataService } from '../../../../services/current-data-service';
import { BaryModalService } from '../../../../services/bary-modal.service';
import { BaryFormFactoryService } from '../../../../services/bary-form-factory.service';
import { TransportExtendedService } from '../../../../services/transport-extended.service';
import { EstimateService } from '../../../../services/estimate.service';

import { FormArray, FormGroup } from '@angular/forms';
import { TRANSPORT_ITEM_ESTIMATE_STEP } from '../../../../models/enums/transport-item-estimate-step.enum';
import { FaDirectiveEnum } from '../../../../models/enums/fa-directive.enum';
import { PricingTransporterConfigurationService } from '../../../../services/pricing-transporter-configuration.service';
import { IPricingTransporterConfiguration } from '../../../../models/pricing-transporter.configuration';
import { TransportStep } from '../../../../models/enums/transport-step.enum';
import { GenericBaryFormGroup, IGenericBaryFormGroup } from '../../../../models/forms/GenericBaryFormGroup';
import { TransportItemControls } from '../../../../models/forms/builder/TransportItemFormBuilder';

@Component({
  selector: 'jhi-wepal-light-estimate',
  templateUrl: './wepal-light-estimate.component.html',
  styleUrls: ['./wepal-light-estimate.component.scss'],
})
export class WepalLightEstimateComponent implements OnInit {
  readonly #router = inject(Router);
  readonly #currentDataService = inject(CurrentDataService);
  readonly #transportService = inject(TransportExtendedService);
  readonly #bff = inject(BaryFormFactoryService);
  readonly #spinnerService = inject(NgxSpinnerService);
  readonly #estimateService = inject(EstimateService);
  readonly #modalService = inject(BaryModalService);
  readonly #cdr = inject(ChangeDetectorRef);
  readonly #pricingTransporterConfigurationService = inject(PricingTransporterConfigurationService);

  @ViewChild('card') card!: ElementRef;

  public transportFormGroup!: TransportFormGroup;
  public quotationOK = false;
  public loadingData = false;
  public offerEstimations: OfferEstimation[] = [];
  public transport!: Transport;
  public spinnerPriceName = 'priceRefreshing';
  public transporter!: Transporter;

  protected readonly FAS = FaDirectiveEnum.FAS;

  private _creatorUuid = CookieConsent.getCookie().consentId;

  public ngOnInit(): void {
    forkJoin({
      transport: this.#transportService.createEmpty(),
      transporter: this.#currentDataService.getCurrentTransporter(),
      customer: this.#currentDataService.getCustomer(),
      config: this.#pricingTransporterConfigurationService.retrieveConfiguration().pipe(mapAsBody),
    }).subscribe(data => {
      this.transporter = data.transporter;
      // build the formGroup
      this.transportFormGroup = this.#bff.makeTransportForm(data.config, data.transporter!, data.transport);
      // define refreshment conditions
      this.defineRefreshingConditions();
      this.refreshOffer();
    });
  }

  // define changes that might refresh the price
  private defineRefreshingConditions(): void {
    this.transportFormGroup
      .getAngularForm()
      .valueChanges.pipe(debounceTime(300))
      .subscribe(() => {
        this.refreshOffer();
      });
  }

  private refreshOffer(): void {
    if (this.transportFormGroup.value() && this.transportFormGroup.controls.items.length) {
      const transport = new Transport(this.transportFormGroup.getAngularForm().getRawValue());
      // this fields is required for saving the transport
      if (!transport.pickupAddressStreet) {
        transport.pickupAddressStreet = '';
      }
      if (!transport.pickupAddressFullname) {
        transport.pickupAddressFullname = '';
      }
      if (!transport.deliveryAddressStreet) {
        transport.deliveryAddressStreet = '';
      }
      if (!transport.deliveryAddressFullname) {
        transport.deliveryAddressFullname = '';
      }
      transport.creatorUuid = this._creatorUuid;
      transport.orderTunnelStep = TransportStep.SEARCH_OFFERS;
      // check
      this.quotationOK = transport.checkItems() && transport.checkAddresses();
      if (!this.quotationOK) {
        this.#spinnerService.hide(this.spinnerPriceName).then(() => (this.loadingData = false));
        return;
      }
      this.#spinnerService.show(this.spinnerPriceName).then(() => (this.loadingData = true));
      this.doEstimatePricing(transport);
    }
  }

  public initTransportItem(index?: number): void {
    const transportItemFormValue: TransportItem = (index || index === 0 ? this.transportFormGroup.controls.items[index].value() : {}) as TransportItem;
    this.#modalService
      .openModalEstimate(this.transportFormGroup, this.currentStep, new TransportItem(transportItemFormValue))
      .action()
      .subscribe(res => {
        if (res.submit) {
          const itemFormGroup = res.value as FormGroup;
          itemFormGroup.controls['transport'].setValue(new Transport({ id: this.transportFormGroup.controls.id.value }));
          const transportItemControlsGenericBaryFormGroup = new GenericBaryFormGroup<TransportItemControls>(
            '' + index,
            itemFormGroup.controls as TransportItemControls,
            itemFormGroup.validator
          );
          if (typeof index === 'number') {
            // MAJ du transportItem
            this.transportFormGroup.controls.items[index].updateForm(transportItemControlsGenericBaryFormGroup.value());
            (this.transportFormGroup.getAngularForm().controls['items'] as FormArray).controls[index] = itemFormGroup;
            // Comme on modifie directement un élément dans le tableau, on ne déclenche pas la condition de rafraîchissement définie dans defineRefreshingConditions
            this.refreshOffer();
          } else {
            // Ajout du transportItem
            // Comme on pousse un élément dans le tableau, on déclenche la condition de rafraîchissement définie dans defineRefreshingConditions
            this.transportFormGroup.controls.items.push(transportItemControlsGenericBaryFormGroup);
            (this.transportFormGroup.getAngularForm().controls['items'] as FormArray).push(itemFormGroup);
          }
        }
      });
  }

  private get currentStep(): TRANSPORT_ITEM_ESTIMATE_STEP {
    if (this.transportFormGroup.controls.isControlledTemperature.value || this.transportFormGroup.controls.isHazardousMaterial.value) {
      return TRANSPORT_ITEM_ESTIMATE_STEP.SPECIAL;
    }
    return this.transportFormGroup.controls.items.length > 0 ? TRANSPORT_ITEM_ESTIMATE_STEP.ITEM : TRANSPORT_ITEM_ESTIMATE_STEP.SETTING;
  }

  public handleRemove(index: number): void {
    this.transportFormGroup.removeTransportItem(index);
    if (this.transportFormGroup.controls.items.length === 0) {
      this.offerEstimations = [];
    }
    this.refreshOffer();
  }

  public updateTransport(offerId: number): void {
    this.transport = new Transport({
      ...this.transportFormGroup.getAngularForm().getRawValue(),
      id: this.transportFormGroup.controls.id.value,
      creatorUuid: this._creatorUuid,
      orderTunnelStep: TransportStep.SEARCH_OFFERS,
    });
    this.#transportService.applyOffer(offerId, this.transport).subscribe({
      next: (newTransport: TransportViewDTO) => {
        this.#pricingTransporterConfigurationService
          .retrieveConfiguration()
          .pipe(mapAsBody)
          .subscribe({
            next: (config: IPricingTransporterConfiguration) => {
              this.transportFormGroup = this.#bff.makeTransportForm(config, this.transporter!, newTransport);
              this.#currentDataService.updateCurrentTransport(newTransport);
              this.#currentDataService.updateTransportInModification(undefined);
              this.#router.navigateByUrl(PATHS.ESTIMATE.path).then();
            },
          });
      },
    });
  }

  private doEstimatePricing(transport: Transport): void {
    this.#estimateService
      .estimatePricing(transport)
      .pipe(mapAsBody)
      .subscribe({
        next: offerEstimations => {
          this.offerEstimations = offerEstimations || [];
          if (this.offerEstimations.length && this.offerEstimations[0].transport) {
            // tracking updateForm transport using back's transport
            this.transportFormGroup.updateForm(this.offerEstimations[0].transport, { emitEvent: false });
          }
          this.card.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
          this.#cdr.detectChanges();
        },
      })
      .add(() => {
        this.#spinnerService.hide(this.spinnerPriceName).then(() => {
          this.loadingData = false;
        });
      });
  }

  public getValueForTransportItem(transportItemForm: IGenericBaryFormGroup<TransportItemControls>): TransportItem {
    return transportItemForm.value();
  }
}
